import {
    AccountInfo,
    AuthenticationResult,
    EventMessage,
    EventType,
    InteractionRequiredAuthError,
    IPublicClientApplication,
} from '@azure/msal-browser';
import { MsalProvider, useAccount, useMsal } from '@azure/msal-react';
import { Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { PropsWithChildren, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { CLIENT_ID } from 'shared/constants';
import { useAuthState } from 'shared/stores';
import { CustomNavigationClient } from 'startup';
import { useHandleAccount } from '../hooks';

export type AuthProviderProps = {
    /**
     * An IPublicClientApplication object to be set as instance on the MSAL provider and handle some event callbacks
     */
    clientApp: IPublicClientApplication;
    /**
     * A function requiring a DefaultUserInfo that returns user info
     * @param user - A DefaultUserInfo object
     * @returns An UserInfo object
     */
};

/**
 *
 * @remarks
 * This provider is designed to handle authentication and token.
 *
 * @returns A component inside a MSAL provider to authenticate the app
 */
export function ProvideAuth({ children, clientApp }: React.PropsWithChildren<AuthProviderProps>) {
    const history = useHistory();
    const { enqueueSnackbar } = useSnackbar();

    const { handleUserAccount, clearUser } = useHandleAccount({
        clientId: CLIENT_ID,
        loginPermission: `${CLIENT_ID}.login`,
    });

    React.useEffect(() => {
        const navigationClient = new CustomNavigationClient(history);
        clientApp.setNavigationClient(navigationClient);

        const callbackId = clientApp.addEventCallback((event: EventMessage) => {
            switch (event.eventType) {
                case EventType.ACQUIRE_TOKEN_SUCCESS:
                    const tokenPayload = event.payload as AuthenticationResult;
                    // Verify Insight claims here.
                    const tokenAccount = tokenPayload.account;
                    if (!tokenPayload.fromCache && tokenAccount) {
                        clientApp.setActiveAccount(tokenAccount);
                        handleUserAccount(tokenAccount);
                    }
                    break;
                case EventType.LOGIN_SUCCESS:
                    const loginPayload = event.payload as AuthenticationResult;
                    // Verify Insight claims here.
                    console.log('LOGIN SUCCESS', event, loginPayload, loginPayload?.account);
                    const loginAccount = loginPayload.account;
                    if (loginAccount) {
                        clientApp.setActiveAccount(loginAccount);
                        handleUserAccount(loginAccount);
                    }
                    break;
                case EventType.LOGOUT_END:
                    clearUser();
                    break;
                case EventType.ACQUIRE_TOKEN_FAILURE:
                    const error = event.error;
                    if (error instanceof InteractionRequiredAuthError) {
                        // console.log('interaction required');
                        // console.log(error);
                    }
                    break;
                default:
                    break;
            }
        });

        return () => {
            if (callbackId) {
                clientApp.removeEventCallback(callbackId);
            }
        };
    }, [clientApp, enqueueSnackbar, handleUserAccount, clearUser, history]);

    return (
        <MsalProvider instance={clientApp}>
            <SelectAccount handleUserAccount={handleUserAccount}>{children}</SelectAccount>
        </MsalProvider>
    );
}
interface SelectAccountProps {
    handleUserAccount: (account: AccountInfo) => void;
}
function SelectAccount({ children, handleUserAccount }: PropsWithChildren<SelectAccountProps>) {
    const authState = useAuthState();
    const user = authState.user.get();
    const activeAccount = useAccount();
    const { accounts, instance } = useMsal();

    useEffect(() => {
        if (!user) {
            if (activeAccount) {
                handleUserAccount(activeAccount);
            } else {
                if (accounts.length) {
                    instance.setActiveAccount(accounts[0]);
                    handleUserAccount(accounts[0]);
                }
            }
        }
    }, [activeAccount, handleUserAccount, user, accounts, instance]);

    return <>{children}</>;
}

/**
 * @returns a MUI h4 Typography component to inform the authorization is in progress
 *
 */
export const AuthInProgress = () => (
    <Typography sx={{ textAlign: 'center' }} variant='h4'>
        Authorization in progress...
    </Typography>
);
